<template>
  <div class="content-section-container" :class="{ 'secret-layout': secret, 'transparent-image': transparentImage }">
    <h2 v-if="heading" class="main-heading">
      <span v-html="heading"></span>
    </h2>

    <p v-if="subheading" class="subheading">{{ subheading }}</p>

    <!-- Secret Layout -->
    <template v-if="secret">
      <!-- Video under subheading -->
      <div v-if="imageSrc || videoUrl" class="secret-video-section">
        <div v-if="videoUrl" class="video-container">
          <iframe
            v-if="videoPlaying"
            class="content-video"
            :src="getEmbedUrl(videoUrl) + '?autoplay=1'"
            title="YouTube video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
          <div v-else class="video-thumbnail" @click="playVideo">
            <img class="content-image" :src="videoThumbnail" alt="Video thumbnail" />
            <div class="play-button">
              <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle class="ring" cx="80" cy="80" r="78" stroke="#fff" stroke-width="1" stroke-opacity="0.6"/>
                <circle class="ring" cx="80" cy="80" r="60" stroke="#fff" stroke-width="1" stroke-opacity="0.85"/>
                <circle cx="80" cy="80" r="34" fill="#fff"/>
                <path d="M92 80L73 92V68L92 80Z" fill="#ff8f37"/>
              </svg>
            </div>
          </div>
        </div>
        <img v-else class="content-image" :src="imageSrc" :alt="imageAlt || t('content_section.default_image_alt')" />
      </div>

      <!-- Two columns: CTA button and content (desktop) -->
      <div class="secret-columns">
        <!-- Content column -->
        <div class="secret-content-column">
          <slot></slot>
        </div>

        <!-- CTA button column -->
        <div class="secret-cta-column">
          <CtaButton v-if="showButton" @click="handleButtonClick" class="section-cta" />
        </div>
      </div>
    </template>

    <!-- Regular Layout -->
    <template v-else>
      <div class="content-wrapper" :class="{ 'reverse': imagePosition === 'left' }">
        <div class="text-cta-wrapper">
          <div class="text-section">
            <slot></slot>
          </div>

          <CtaButton v-if="showButton" @click="handleButtonClick" class="section-cta section-cta-desktop" />
        </div>

        <div v-if="imageSrc || videoUrl" class="image-section">
          <div v-if="videoUrl" class="video-container">
            <iframe
              v-if="videoPlaying"
              class="content-video"
              :src="getEmbedUrl(videoUrl) + '?autoplay=1'"
              title="YouTube video"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              allowfullscreen
            ></iframe>
            <div v-else class="video-thumbnail" @click="playVideo">
              <img class="content-image" :src="videoThumbnail" alt="Video thumbnail" />
              <div class="play-button">
                <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle class="ring" cx="80" cy="80" r="78" stroke="#fff" stroke-width="1" stroke-opacity="0.6"/>
                  <circle class="ring" cx="80" cy="80" r="60" stroke="#fff" stroke-width="1" stroke-opacity="0.85"/>
                  <circle cx="80" cy="80" r="34" fill="#fff"/>
                  <path d="M92 80L73 92V68L92 80Z" fill="#ff8f37"/>
                </svg>
              </div>
            </div>
          </div>
          <img v-else class="content-image" :src="imageSrc" :alt="imageAlt || t('content_section.default_image_alt')" />
        </div>
      </div>

      <CtaButton v-if="showButton" @click="handleButtonClick" class="section-cta section-cta-mobile" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useLanguage } from '../../composables/useLanguage'
import CtaButton from './CtaButton.vue'

const { t } = useLanguage()

interface Props {
  heading?: string
  subheading?: string
  imageSrc?: string
  imageAlt?: string
  videoUrl?: string
  videoThumbnail?: string
  imagePosition?: 'left' | 'right'
  showButton?: boolean
  secret?: boolean
  transparentImage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  heading: '',
  subheading: '',
  imageSrc: '',
  imageAlt: '',
  videoUrl: '',
  videoThumbnail: '',
  imagePosition: 'right',
  showButton: false,
  secret: false,
  transparentImage: false
})

const videoPlaying = ref(false)

const getEmbedUrl = (url: string) => {
  const videoId = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/)?.[1]
  return `https://www.youtube.com/embed/${videoId}`
}

const playVideo = () => {
  videoPlaying.value = true
}

const emit = defineEmits<{
  buttonClick: []
}>()

const handleButtonClick = () => {
  emit('buttonClick')
}
</script>

<style scoped>
.content-section-container {
  width: calc(100% - var(--spacing-lg));
  max-width: 1450px;
  margin: var(--spacing-2xl) auto;
  position: relative;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);
  border-radius: clamp(20px, 4vw, 40px);
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border: 2px solid rgba(251, 185, 150, 0.3);
  box-sizing: border-box;
  padding: var(--spacing-lg) var(--spacing-xl);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
}

html[lang="ar"] .content-section-container {
  text-align: right;
  direction: rtl;
}

html[lang="en"] .content-section-container {
  text-align: left;
  direction: ltr;
}

.content-section-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 20%;
  transform: translate(-50%, -50%);
  filter: blur(clamp(150px, 30vw, 300px));
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 149, 255, 0.3), rgba(255, 143, 55, 0.2));
  width: clamp(250px, 50vw, 500px);
  height: clamp(200px, 40vw, 400px);
  pointer-events: none;
  z-index: 0;
}

.content-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xl);
  z-index: 1;
  width: 100%;
  flex-direction: row;
}

/* When imagePosition="left", reverse to put image on left */
.content-wrapper.reverse {
  flex-direction: row-reverse;
}

.text-section {
  flex: 1;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vw, 12px);
  min-width: 0;
  text-align: start;
  width: 100%;
}

/* Deep styles for slotted content sections */
.text-section :deep(.quotes-section),
.text-section :deep(.truth-section),
.text-section :deep(.result-section),
.text-section :deep(.immersion-content),
.text-section :deep(.mother-tongue-content),
.text-section :deep(.custom-content),
.text-section :deep(.trial-section-content) {
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.8vw, 6px);
}

.text-cta-wrapper {
  flex: 1;
  max-width: 600px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(15px, 2.5vw, 20px);
  align-items: center;
  width: 100%;
}

.section-cta {
  width: 100%;
  max-width: 600px;
}

.section-cta-mobile {
  display: none;
}

.section-cta-desktop {
  display: block;
}

.main-heading {
  font-size: clamp(20px, 3vw, 34px);
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 clamp(8px, 1.2vw, 12px) 0;
  color: #000;
  width: 100%;
  text-align: center;
}

.main-heading :deep(.highlight) {
  color: #ff8f37;
}

.subheading {
  font-size: clamp(13px, 1.4vw, 18px);
  line-height: 1.5;
  margin: 0 0 clamp(8px, 1vw, 12px) 0;
  color: #333;
  width: 100%;
  max-width: 1000px;
  font-weight: 500;
  text-align: center;
}

.image-section {
  flex: 1;
  max-width: 650px;
  min-width: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-radius: clamp(20px, 3vw, 30px);
  padding: clamp(10px, 2vw, 20px);
}

.transparent-image .image-section,
.transparent-image .secret-video-section {
  background: transparent;
  padding: 0;
}

.content-image {
  width: 100%;
  height: auto;
  border-radius: clamp(20px, 3vw, 30px);
  object-fit: cover;
}

.video-container {
  width: 100%;
  position: relative;
}

.content-video {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: clamp(20px, 3vw, 30px);
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15);
}

.video-thumbnail {
  position: relative;
  cursor: pointer;
  width: 100%;
}

.video-thumbnail:hover .play-button {
  transform: translate(-50%, -50%) scale(1.1);
}

.play-button .ring {
  transition: stroke 0.2s ease, stroke-opacity 0.2s ease;
}

.video-thumbnail:hover .play-button .ring {
  stroke: #ff8f37;
  stroke-opacity: 1;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease;
  pointer-events: none;
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3));
}


/* Secret Layout Styles */
.secret-layout .secret-video-section {
  width: 100%;
  margin: clamp(20px, 3vw, 30px) 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-radius: clamp(20px, 3vw, 30px);
  padding: clamp(10px, 2vw, 20px);
}

.secret-columns {
  display: flex;
  flex-direction: row;
  gap: clamp(30px, 4vw, 50px);
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: clamp(30px, 4vw, 40px);
}

.secret-content-column {
  flex: 1;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vw, 12px);
  text-align: start;
}

.secret-cta-column {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Deep styles for slotted content in secret layout */
.secret-content-column :deep(.quotes-section),
.secret-content-column :deep(.truth-section),
.secret-content-column :deep(.result-section),
.secret-content-column :deep(.immersion-content),
.secret-content-column :deep(.mother-tongue-content),
.secret-content-column :deep(.custom-content),
.secret-content-column :deep(.trial-section-content) {
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.8vw, 6px);
}

@media (max-width: 1200px) {
  .content-section-container::before {
    opacity: 0.8;
  }
}

@media (max-width: 968px) {
  .content-wrapper {
    flex-direction: column !important;
    gap: clamp(25px, 5vw, 40px);
  }

  .text-cta-wrapper {
    max-width: 100%;
    order: 1;
  }

  .text-section {
    max-width: 100%;
  }

  .image-section {
    max-width: 100%;
    width: 100%;
    order: 2;
  }

  .main-heading {
    margin-bottom: clamp(12px, 2vw, 18px);
  }

  .section-cta-desktop {
    display: none;
  }

  .section-cta-mobile {
    display: block;
    margin-top: clamp(15px, 2.5vw, 20px);
  }

  /* Secret layout mobile adjustments */
  .secret-columns {
    flex-direction: column;
    gap: clamp(20px, 3vw, 30px);
  }

  .secret-content-column {
    max-width: 100%;
    order: 1;
    text-align: center;
  }

  .secret-cta-column {
    max-width: 100%;
    width: 100%;
    order: 2;
  }

  .secret-layout .secret-video-section {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .content-section-container {
    margin: clamp(20px, 4vw, 30px) clamp(15px, 3vw, 20px);
    padding: clamp(20px, 4vw, 30px) clamp(15px, 3vw, 20px);
  }

  .content-wrapper {
    gap: clamp(20px, 4vw, 30px);
  }
}

@media (max-width: 640px) {
  .content-section-container {
    padding: clamp(15px, 3vw, 25px) clamp(12px, 2.5vw, 20px);
    margin: clamp(15px, 3vw, 25px) clamp(10px, 2vw, 15px);
  }

  .content-wrapper {
    gap: clamp(15px, 3vw, 25px);
  }
}

@media (max-width: 480px) {
  .content-section-container {
    border-radius: clamp(14px, 3.5vw, 18px);
    margin: clamp(15px, 3vw, 20px) clamp(10px, 2vw, 12px);
    padding: clamp(12px, 2.5vw, 16px) clamp(10px, 2vw, 14px);
  }

  .content-wrapper {
    gap: clamp(12px, 2.5vw, 18px);
  }

  .content-image {
    border-radius: clamp(12px, 2vw, 16px);
  }

  .text-section {
    gap: clamp(6px, 1.2vw, 10px);
  }
}

@media (max-width: 380px) {
  .content-section-container {
    border-radius: 12px;
    margin: 12px 8px;
    padding: 10px 8px;
  }

  .content-section-container::before {
    opacity: 0.5;
  }

  .content-wrapper {
    gap: 10px;
  }
}
</style>
